import React, { useMemo, useState, useCallback } from "react";
import classNames from "classnames";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Form,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetTenantUsers } from "api/users/service";
import Select from "components/Select";
import { useNavigate } from "react-router-dom";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { formatDateTimeForDisplay } from "../../utils/dateUtils";
import { useTranslation } from "react-i18next";
import {
  getTheme2MultiSelectSelectConfig,
  getTheme2SearchFilterColProps,
  getTheme2SearchFilterFieldCss,
  getTheme2SearchFilterMultiSelectStyles,
  getTheme2MultiSelectCss,
} from "utils/theme2MultiSelect";

const TENANT_LOGS_USER_PREFIX = "tenant-logs-user";

const hasChangedData = (changedData) => {
  if (!changedData || changedData === "null") return false;
  if (typeof changedData === "object") {
    return Object.keys(changedData).length > 0;
  }
  if (typeof changedData === "string") {
    return (
      changedData.length > 0 &&
      changedData !== "[]" &&
      changedData !== "{}"
    );
  }
  return false;
};

const TENANT_LOGS_TAB_STYLES = (isTheme2) => `
  ${isTheme2 ? `
  ${getTheme2SearchFilterFieldCss({
    pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .tenant-logs-tab",
    filtersSelector: ".tenant-logs-filters",
    rowClass: "search-logs-filters-row",
    colClass: "search-logs-filter-col",
  })}
  .tenant-edit-page.tenant-edit-page--theme2 .tenant-logs-tab .search-logs-filters-row {
    column-gap: 4px;
  }
  .tenant-edit-page.tenant-edit-page--theme2 .tenant-logs-tab .search-logs-filter-col {
    padding-left: 4px;
    padding-right: 4px;
  }
  .tenant-edit-page.tenant-edit-page--theme2 .tenant-logs-tab .search-logs-filter-col:first-child {
    padding-left: 0;
  }
  ${getTheme2MultiSelectCss({
    prefix: TENANT_LOGS_USER_PREFIX,
    pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .tenant-logs-tab",
  })}
  ` : ""}
`;

const TenantLogs = ({
  activeColor = "secondary",
  tenantId = null,
  logsData = null,
  onPageChange = null,
  onPageSizeChange = null,
  isTheme2 = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [selectedUser, setSelectedUser] = useState(null);

  const userMultiSelectStyles = useMemo(
    () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const userTheme2Config = useMemo(
    () => getTheme2MultiSelectSelectConfig(TENANT_LOGS_USER_PREFIX, isTheme2),
    [isTheme2]
  );
  const filterColProps = getTheme2SearchFilterColProps(isTheme2, {
    filterColClass: "search-logs-filter-col",
    md: "4",
  });
  const userColProps = isTheme2 ? filterColProps : { md: "4", xs: "12" };

  const { data: usersData } = useGetTenantUsers(1, 100);

  const data = logsData;
  const isLoading = !logsData;

  const userOptions = useMemo(() => {
    return usersData?.results?.map(user => ({
      value: user.id,
      label: user.user_detail?.full_name
    })) || [];
  }, [usersData]);

  const [expandedRows, setExpandedRows] = useState({});
  const LogsTable = isTheme2 ? ReactTable2 : ReactTable;

  const toggleExpand = useCallback((id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const renderChangedData = useCallback((changedData) => {
    if (!changedData) return <span>-</span>;

    let data = changedData;
    if (typeof changedData === "string") {
      try {
        data = JSON.parse(changedData);
      } catch (e) {
        return <span>{changedData}</span>;
      }
    }

    if (typeof data === "object" && !Array.isArray(data)) {
      return (
        <div className="pl-2">
          {Object.entries(data).map(([key, value], idx) => (
            <div key={idx} className="mb-1" style={{ fontSize: '13px' }}>
              <strong style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}:</strong> {String(value)}
            </div>
          ))}
        </div>
      );
    }

    const nodes = Array.isArray(data) ? data : [];
    if (!nodes.length) return <span>-</span>;

    return (
      <div className="pl-2">
        {nodes.map((node, idx) => {
          if (typeof node === "object" && node.children) {
            const text = (node?.children || [])
              .map((c) => (c && typeof c.text === "string" ? c.text : ""))
              .join("");
            return <p key={idx} className="mb-1">{text || "-"}</p>;
          }
          if (typeof node === "object") {
            return (
              <div key={idx} className="mb-2 border-bottom pb-2">
                {Object.entries(node).map(([k, v], i) => (
                  <div key={i} className="mb-1" style={{ fontSize: '13px' }}>
                    <strong style={{ textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}:</strong> {String(v)}
                  </div>
                ))}
              </div>
            );
          }
          return <p key={idx} className="mb-1">{String(node)}</p>;
        })}
      </div>
    );
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const list = data?.logs || data?.results || data?.data || data || [];
    return (Array.isArray(list) ? list : []).map((log, idx) => ({
      id: log?.id ?? log?.documentId ?? idx + 1,
      userName: log?.user?.user_detail?.full_name || "-",
      userEmail: log?.user?.email || "-",
      userId: log?.user?.id || "-",
      action: log?.action || "-",
      link: log?.link || null,
      createdAt: log?.created_at || log?.createdAt || "-",
      changedData: log?.changed_data,
    }));
  }, [data]);

  const columns = [
    {
      Header: t('logs.userName'),
      accessor: "userName",
      Cell: ({ row }) => {
        const { original } = row;
        return (
          <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate(`/tenant/${tenantId}/user/${original.userId}`)}>{original.userName}</span>
        );
      },
    },
    {
      Header: t('logs.email'),
      accessor: "userEmail",
    },
    {
      Header: t('logs.action'),
      accessor: "action",
      Cell: ({ row }) => {
        const { original } = row;
        const clickable = !!original.link;
        return (
          <span
            style={{ cursor: clickable ? "pointer" : "default", textDecoration: clickable ? "underline" : "none", color: clickable ? activeColorStyle.backgroundColor : undefined }}
            onClick={() => {
              if (original.link) navigate(original.link);
            }}
          >
            {original.action}
          </span>
        );
      },
    },
    {
      Header: t('logs.date'),
      accessor: "createdAt",
      Cell: ({ value }) => formatDateTimeForDisplay(value),
    },
    {
      Header: "",
      accessor: "id",
      disableSortBy: true,
      width: 40,
      Cell: ({ row }) => {
        const { original } = row;
        const isOpen = !!expandedRows[original.id];
        if (!hasChangedData(original.changedData)) return null;

        return (
          <span
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(original.id);
            }}
            style={{
              cursor: "pointer",
              display: "inline-block",
              transform: isOpen ? "rotate(270deg)" : "rotate(90deg)",
              transition: "transform 0.15s ease",
            }}
          >
            ▶
          </span>
        );
      },
    },
  ];

  const tableProps = {
    activeColor,
    data: rows,
    columns,
    disableViewportClamp: true,
    manualPagination: true,
    pageCount: data?.pagination?.pageCount || 1,
    pageIndex: (data?.pagination?.page || 1) - 1,
    totalItems: data?.pagination?.total,
    pageSize: data?.pagination?.pageSize || 10,
    noDataText: t("logs.noRecordsFound"),
    loading: isLoading,
    loadingText: t("logs.loading"),
    onPageChange: (pageIndex) => {
      if (onPageChange) {
        onPageChange(pageIndex + 1);
      }
    },
    onPageSizeChange: (size) => {
      if (onPageSizeChange) {
        onPageSizeChange(size);
      }
    },
    renderExpandedRow: (original) => (
      <div className="p-2">{renderChangedData(original.changedData)}</div>
    ),
    isRowExpanded: (original) => !!expandedRows[original.id],
  };

  return (
    <div
      className={classNames("tenant-logs-tab", {
        "tenant-logs-tab--theme2": isTheme2,
      })}
    >
      <style>{TENANT_LOGS_TAB_STYLES(isTheme2)}</style>
      <Card className="shadow-none m-0 border-0">
        <CardHeader className="pb-0">
          <div className="tenant-logs-filters">
            <Form>
              <Row className={isTheme2 ? "search-logs-filters-row mb-3" : "mb-3"}>
                <Col {...userColProps}>
                  <FormGroup className="mb-0">
                    <Label>{t('reports.filterByUser') || 'Filter by User:'}</Label>
                    <Select
                      activeColor={activeColor}
                      className={isTheme2 ? userTheme2Config.controlClassName : "react-select w-100"}
                      styles={userMultiSelectStyles}
                      placeholder={t('reports.filterByUser')}
                      name="userFilter"
                      isSearchable
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={userTheme2Config.hideSelectedOptions}
                      value={selectedUser}
                      onChange={(option) => setSelectedUser(option)}
                      options={userOptions}
                      isClearable
                      components={userTheme2Config.components}
                      classNames={userTheme2Config.classNames}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </div>
          {!isTheme2 && !isLoading && data?.pagination?.total > 0 && (
            <div style={{ fontWeight: 600, fontSize: ".8rem", color: "#333", letterSpacing: "0.02em", textTransform: "uppercase" }}>
              {data?.pagination?.total} {t('searchDeals.recordsInTotal')}
            </div>
          )}
        </CardHeader>
        <CardBody className="pt-0">
          <div
            className="inventory-table-wrap"
            style={{ fontSize: isTheme2 ? "13px" : "12px", minWidth: 0, maxWidth: "100%" }}
          >
            {isTheme2 ? (
              <LogsTable {...tableProps} className="-striped -highlight sticky-stock-table" />
            ) : (
              <LogsTable
                {...tableProps}
                showPaginationTop
                showPaginationBottom={false}
                className="-striped -highlight"
              />
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default TenantLogs;
